import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  TextField,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button as MuiButton,
  useTheme,
  useMediaQuery,
  Autocomplete,
  MenuItem,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import FormContainer from '../../../common/FormContainer/FormContainer';
import Button from '../../../common/Button/Button';
import { setSubmitDetails } from '../../../store/slices/ideaSubmissionSlice';
import districtsData from '../../../../district.json';

/* ---------------- Helpers ---------------- */
const isValidIndianMobile = (value) => /^[6-9]\d{9}$/.test(value);
const MAX_TEAM_MEMBERS = 5;

const whiteInputSx = {
  '& .MuiInputBase-root': {
    backgroundColor: '#ffffff',
    borderRadius: '14px'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#cbd5e1'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#94a3b8'
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#3b82f6'
  }
};

export function SubmitDetailsStep({ onNext, onBack, submitDetailsConfig }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const storedDetails = useSelector(
    (state) => state.ideaSubmission.submitDetails
  );

  const [localDetails, setLocalDetails] = useState({
    ...storedDetails,
    teamMembers: storedDetails.teamMembers || []
  });

  const [teamInput, setTeamInput] = useState('');
  const [isValid, setIsValid] = useState(false);

  const districtOptions = districtsData.regions;

  /* ---------------- Validation ---------------- */
  useEffect(() => {
    let valid = submitDetailsConfig.every((field) => {
      if (!field.required) return true;

      const value = localDetails[field.key];
      if (!value) return false;

      if (field.key === 'phone') {
        return isValidIndianMobile(value);
      }

      return value.toString().trim().length > 0;
    });

    if (localDetails.isTeam === 'Yes') {
      valid = valid && localDetails.teamMembers.length > 0;
    }

    setIsValid(valid);
  }, [localDetails, submitDetailsConfig]);

  /* ---------------- Handlers ---------------- */
  const handleChange = (key, value) => {
    setLocalDetails((prev) => ({ ...prev, [key]: value }));
  };

  const addTeamMember = () => {
    const name = teamInput.trim();
    if (
      !name ||
      localDetails.teamMembers.length >= MAX_TEAM_MEMBERS ||
      localDetails.teamMembers.includes(name)
    ) {
      return;
    }

    setLocalDetails((prev) => ({
      ...prev,
      teamMembers: [...prev.teamMembers, name]
    }));

    setTeamInput('');
  };

  const getAffiliationLabel = (gradeGroup) => {
  if (!gradeGroup) return 'Affiliation';

  if (gradeGroup === '6 and below' || gradeGroup === '6 - 12') {
    return 'School Name';
  }

  if (gradeGroup === 'Graduate / Undergraduate / Postgraduate') {
    return 'College / University Name';
  }

  if (gradeGroup === 'Working Professional') {
    return 'Organization / Workplace';
  }

  return 'Affiliation';
};


  const removeTeamMember = (name) => {
    setLocalDetails((prev) => ({
      ...prev,
      teamMembers: prev.teamMembers.filter((m) => m !== name)
    }));
  };

  const handleNext = () => {
    dispatch(setSubmitDetails(localDetails));
    onNext();
  };

  /* ---------------- UI ---------------- */
  return (
    <FormContainer>
      {/* Back */}
      {/* <MuiButton onClick={onBack} sx={{ mb: 3 }}>
        Back
      </MuiButton> */}

      <Typography variant="h5" fontWeight={700} mb={3} textAlign="center">
        Your Details
      </Typography>

      {/* Fields */}
      {submitDetailsConfig.map((field) => {
        const value = localDetails[field.key] || '';

        return (
          <Box key={field.key} sx={{ mb: 3 }}>
            {/* TEXT */}
            {field.type === 'text' && field.key !== 'phone' && field.key !== 'affiliation' &&  (
              <TextField
                fullWidth
                label={field.label}
                value={value}
                required={field.required}
                onChange={(e) =>
                  handleChange(field.key, e.target.value)
                }
                sx={whiteInputSx}
              />
            )}

            {/* PHONE */}
            {field.key === 'phone' && (
              <TextField
                fullWidth
                label={field.label}
                value={value}
                required
                error={value && !isValidIndianMobile(value)}
                helperText={
                  value && !isValidIndianMobile(value)
                    ? 'Enter a valid 10-digit Indian mobile number'
                    : ' '
                }
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 10
                }}
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/\D/g, '');
                  handleChange(field.key, onlyNumbers);
                }}
                sx={whiteInputSx}
              />
            )}

            {/* AFFILIATION (Conditional) */}
{field.key === 'affiliation' && localDetails.gradeGroup && (
  <TextField
    fullWidth
    label={getAffiliationLabel(localDetails.gradeGroup)}
    value={value}
    required
    onChange={(e) =>
      handleChange(field.key, e.target.value)
    }
    sx={whiteInputSx}
  />
)}


            {/* DISTRICT */}
            {field.type === 'autocomplete' && (
  <Autocomplete
    options={districtOptions}
    getOptionLabel={(opt) => opt.district_name}
    value={
      districtOptions.find(
        (d) => d.district_name === value
      ) || null
    }
    onChange={(_, newValue) =>
      handleChange(
        field.key,
        newValue?.district_name || ''
      )
    }
    renderInput={(params) => (
      <TextField
        {...params}
        label={field.label}
        required
        sx={whiteInputSx}
      />
    )}
  />
)}


            {/* SELECT */}
            {field.type === 'select' && (
              <TextField
                select
                fullWidth
                label={field.label}
                value={value}
                required
                onChange={(e) =>
                  handleChange(field.key, e.target.value)
                }
                sx={whiteInputSx}
              >
                {field.options.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {/* RADIO */}
            {field.type === 'radio' && (
              <>
                <Typography fontWeight={600} mb={1}>
                  {field.label} *
                </Typography>

                <RadioGroup
                  row={!isMobile}
                  value={value}
                  onChange={(e) =>
                    handleChange(field.key, e.target.value)
                  }
                >
                  {field.options.map((opt) => (
                    <FormControlLabel
                      key={opt}
                      value={opt}
                      control={<Radio />}
                      label={opt}
                    />
                  ))}
                </RadioGroup>
              </>
            )}

            {/* TEAM MEMBERS */}
            {field.key === 'isTeam' &&
              localDetails.isTeam === 'Yes' && (
                <Box mt={2}>
                  <Typography fontWeight={600} mb={1}>
                    Team Members (max 5)
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder="Enter team member name and press Enter"
                    value={teamInput}
                    onChange={(e) => setTeamInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTeamMember();
                      }
                    }}
                    sx={whiteInputSx}
                  />

                  <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                    {localDetails.teamMembers.map((member) => (
                      <Chip
                        key={member}
                        label={member}
                        onDelete={() => removeTeamMember(member)}
                        deleteIcon={<CloseIcon />}
                      />
                    ))}
                  </Box>
                </Box>
              )}
          </Box>
        );
      })}

      {/* Actions */}
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={2}
        justifyContent="space-between"
        mt={4}
      >
        <MuiButton fullWidth={isMobile} onClick={onBack}>
          Back
        </MuiButton>

        <Button
          fullWidth={isMobile}
          onClick={handleNext}
          disabled={!isValid}
        >
          Next
        </Button>
      </Box>
    </FormContainer>
  );
}
